import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import Header from '../../components/Header';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { grievancesAPI } from '../../utils/api';

const AdminGrievances = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [activeTab, setActiveTab] = useState('dashboard');
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  /* =======================
     LOAD DATA
     ======================= */
  useEffect(() => {
    loadGrievances();
  }, [filterStatus]);

  const loadGrievances = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus && filterStatus !== 'all') {
        filters.status = filterStatus;
      }
      const res = await grievancesAPI.getAll(filters);
      setGrievances(res.data || []);
    } catch {
      toast.error('Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     DASHBOARD STATS
     ======================= */
  const grievanceStats = useMemo(() => {
    const stats = { total: 0, pending: 0, in_progress: 0, resolved: 0 };
    grievances.forEach(g => {
      stats.total++;
      if (g.status === 'pending') stats.pending++;
      if (g.status === 'in_progress') stats.in_progress++;
      if (g.status === 'resolved') stats.resolved++;
    });
    return stats;
  }, [grievances]);

  /* =======================
     PIE: SERVICE-WISE COUNT
     ======================= */
  const serviceWisePie = useMemo(() => {
    const map = {};
    grievances.forEach(g => {
      const key = g.service_name || 'Unknown Service';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [grievances]);

  // Colors for service pie
  const SERVICE_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#ef4444', '#14b8a6'];

  /* =======================
     STACKED BAR: OPEN VS RESOLVED
     ======================= */
  const serviceStacked = useMemo(() => {
    const map = {};
    grievances.forEach(g => {
      const s = g.service_name || 'Unknown';
      if (!map[s]) map[s] = { service: s, open: 0, resolved: 0 };
      if (g.status === 'resolved') map[s].resolved++;
      else map[s].open++;
    });
    return Object.values(map);
  }, [grievances]);

  /* =======================
     AVG TAT SERVICE-WISE
     ======================= */
  const avgTatByService = useMemo(() => {
    const map = {};
    grievances.forEach(g => {
      if (g.status !== 'resolved' || !g.tat_seconds) return;
      const s = g.service_name || 'Unknown';
      if (!map[s]) map[s] = { service: s, total: 0, count: 0 };
      map[s].total += g.tat_seconds;
      map[s].count++;
    });

    return Object.values(map).map(i => ({
      service: i.service,
      avgTatHours: +(i.total / i.count / 3600).toFixed(2),
    }));
  }, [grievances]);

  /* =======================
     STATUS BAR CHART DATA
     ======================= */
  const statusBarData = useMemo(() => [
    { status: 'Pending', count: grievanceStats.pending, fill: '#f59e0b' },
    { status: 'In Progress', count: grievanceStats.in_progress, fill: '#3b82f6' },
    { status: 'Resolved', count: grievanceStats.resolved, fill: '#10b981' },
  ], [grievanceStats]);

  /* =======================
     UPDATE GRIEVANCE
     ======================= */
  const handleUpdate = async () => {
    try {
      await grievancesAPI.update(selectedGrievance.id, {
        status: newStatus,
        admin_notes: adminNotes,
      });
      toast.success('Grievance updated successfully');
      setDialogOpen(false);
      loadGrievances();
    } catch {
      toast.error('Update failed');
    }
  };

  const getStatusColor = status =>
    ({
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
    }[status] || 'bg-gray-100');

  /* =======================
     CUSTOM TOOLTIPS
     ======================= */
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-xl border-2 border-gray-100">
          <p className="font-semibold text-gray-900 mb-1">
            {payload[0].name || label}
          </p>
          <p className="text-sm text-gray-600">
            Count: <span className="font-bold text-gray-900">{payload[0].value}</span>
          </p>
          {grievanceStats.total > 0 && payload[0].payload?.value && (
            <p className="text-xs text-gray-500 mt-1">
              {((payload[0].value / grievanceStats.total) * 100).toFixed(1)}% of total
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null;

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="font-bold text-sm drop-shadow-lg"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  /* =======================
     UI
     ======================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      <Header />

      <div className="flex-1 px-4 sm:px-6 lg:px-12 py-8 w-full">
        <div className="max-w-[1800px] mx-auto">
          {/* HEADER */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {t('grievancesManagement')}
            </h1>
            <p className="text-gray-600">
              Monitor and manage all grievances from a comprehensive dashboard
            </p>
          </div>

          {/* TAB NAVIGATION */}
          <div className="flex gap-2 mb-8 bg-white p-1 rounded-lg shadow-sm w-fit">
            {[
              { key: 'dashboard', label: 'Dashboard', icon: '📊' },
              { key: 'table', label: 'Grievance List', icon: '📋' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* =======================
              DASHBOARD TAB
              ======================= */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">

              <div className="flex gap-4 items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-5">
                <Label className="font-semibold text-gray-700">Filter:</Label>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">From:</Label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">To:</Label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* SUMMARY CARDS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    label: 'Total Grievances',
                    value: grievanceStats.total,
                    icon: '📝',
                    gradient: 'from-purple-500 to-purple-600',
                    bg: 'bg-purple-50',
                  },
                  {
                    label: 'Pending',
                    value: grievanceStats.pending,
                    icon: '⏳',
                    gradient: 'from-yellow-500 to-orange-500',
                    bg: 'bg-yellow-50',
                  },
                  {
                    label: 'In Progress',
                    value: grievanceStats.in_progress,
                    icon: '🔄',
                    gradient: 'from-blue-500 to-blue-600',
                    bg: 'bg-blue-50',
                  },
                  {
                    label: 'Resolved',
                    value: grievanceStats.resolved,
                    icon: '✅',
                    gradient: 'from-green-500 to-emerald-600',
                    bg: 'bg-green-50',
                  },
                ].map(card => (
                  <div
                    key={card.label}
                    className={`${card.bg} rounded-2xl p-6 border-2 border-white shadow-lg hover:shadow-xl transition-shadow duration-300`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-3xl">{card.icon}</span>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center text-white font-bold shadow-md`}>
                        {card.value}
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-600">{card.label}</p>
                  </div>
                ))}
              </div>

              {/* ROW 1: STATUS BAR & SERVICE PIE */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* STATUS BAR CHART */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Grievances by Status</h2>
                      <p className="text-sm text-gray-500 mt-1">Count across all statuses</p>
                    </div>
                    <span className="text-2xl">📊</span>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={statusBarData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="status"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: '#d1d5db' }}
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="count"
                        radius={[12, 12, 0, 0]}
                        animationBegin={0}
                        animationDuration={1200}
                        animationEasing="ease-out"
                      >
                        {statusBarData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* SERVICE-WISE PIE */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Service-wise Distribution</h2>
                      <p className="text-sm text-gray-500 mt-1">Grievances per service category</p>
                    </div>
                    <span className="text-2xl">🔧</span>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={serviceWisePie}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={CustomPieLabel}
                        outerRadius={110}
                        dataKey="value"
                        nameKey="name"
                        animationBegin={200}
                        animationDuration={1000}
                        animationEasing="ease-out"
                      >
                        {serviceWisePie.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        wrapperStyle={{ fontSize: '12px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ROW 2: OPEN VS RESOLVED STACKED BAR */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Open vs Resolved</h2>
                    <p className="text-sm text-gray-500 mt-1">Service-wise resolution status</p>
                  </div>
                  <span className="text-2xl">⚖️</span>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={serviceStacked}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="service"
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="open"
                      stackId="a"
                      fill="#f59e0b"
                      radius={[0, 0, 0, 0]}
                      animationBegin={0}
                      animationDuration={1200}
                    />
                    <Bar
                      dataKey="resolved"
                      stackId="a"
                      fill="#10b981"
                      radius={[12, 12, 0, 0]}
                      animationBegin={0}
                      animationDuration={1200}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* ROW 3: AVERAGE TAT CHART */}
              {avgTatByService.length > 0 && (
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Average Turnaround Time</h2>
                      <p className="text-sm text-gray-500 mt-1">Mean resolution time by service (in hours)</p>
                    </div>
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={avgTatByService}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="service"
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                        label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#6b7280' }}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white px-4 py-3 rounded-lg shadow-xl border-2 border-gray-100">
                                <p className="font-semibold text-gray-900 mb-1">{payload[0].payload.service}</p>
                                <p className="text-sm text-gray-600">
                                  Avg TAT: <span className="font-bold text-blue-600">{payload[0].value} hours</span>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="avgTatHours"
                        fill="#3b82f6"
                        radius={[12, 12, 0, 0]}
                        animationBegin={400}
                        animationDuration={1200}
                        animationEasing="ease-out"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* =======================
              TABLE TAB
              ======================= */}
          {activeTab === 'table' && (
            <div className="space-y-6">
              {/* FILTER */}
              <div className="flex gap-4 items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-5">
                <Label className="font-semibold text-gray-700">Filter:</Label>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">From:</Label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">To:</Label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-auto text-sm text-gray-600">
                  Showing <span className="font-bold">{grievances.length}</span> grievance(s)
                </div>
              </div>

              {/* TABLE */}
              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                </div>
              ) : grievances.length === 0 ? (
                <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
                  <span className="text-6xl mb-4 block">📭</span>
                  <p className="text-gray-500 text-lg">No grievances found</p>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                        <tr>
                          {['Reference ID', 'Name', 'Contact', 'Message', 'Service Type','Status', 'Actions'].map(h => (
                            <th key={h} className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-700">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {grievances.map(g => (
                          <tr key={g.id} className="hover:bg-blue-50 transition-colors duration-150">
                            <td className="px-6 py-4 font-mono text-sm font-semibold text-blue-600">
                              {g.reference_id}
                            </td>
                            <td className="px-6 py-4 font-medium text-gray-900">{g.name}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span>📧</span>
                                  <span>{g.email}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span>📱</span>
                                  <span>{g.phone}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="max-w-xs">
                                <p className="text-sm text-gray-900 truncate font-medium mb-1">{g.subject}</p>
                                <p className="text-sm text-gray-500 truncate">{g.message}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1.5 text-xs font-bold rounded-full`}>
                                {g.service_name}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-3 py-1.5 text-xs font-bold rounded-full ${getStatusColor(g.status)}`}>
                                {g.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <Button
                                size="sm"
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                                onClick={() => {
                                  setSelectedGrievance(g);
                                  setNewStatus(g.status);
                                  setAdminNotes(g.admin_notes || '');
                                  setDialogOpen(true);
                                }}
                              >
                                Update
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* UPDATE DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Update Grievance</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700">Reference ID</Label>
              <div className="text-lg font-mono font-bold text-blue-600 mt-1">
                {selectedGrievance?.reference_id}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700">Message</Label>
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 mt-2 border border-gray-200">
                {selectedGrievance?.message}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700 mb-2 block">Admin Notes</Label>
              <Textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                rows={4}
                placeholder="Add internal notes or resolution details..."
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} className="bg-blue-500 hover:bg-blue-600">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGrievances;